import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate, addDaysIso } from "@/lib/dates";
import { fmtMoney, todayIso, fmtDecimalHours } from "@/lib/time";
import { DateInput } from "@/components/DateInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

const RC_NOTE = {
  LT: "PVM mokėtojas yra pirkėjas (atvirkštinis apmokestinimas, PVMĮ 96 str.).",
  EN: "Reverse charge – VAT to be accounted for by the buyer.",
};

const lineKey = (l) => `${l.source_type}-${l.source_id}`;

export default function InvoiceForm() {
  const { id } = useParams();
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(null);
  const [matter, setMatter] = useState(null);
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState({ time_entries: [], expenses: [] });
  const [lines, setLines] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = (await api.get("/settings")).data;
        setSettings(s);
        if (isEdit) {
          const inv = (await api.get(`/invoices/${id}`)).data;
          if (inv.status !== "Draft") {
            toast.error(t("invoice.only_draft_edit"));
            navigate(`/invoices/${id}`, { replace: true });
            return;
          }
          const m = (await api.get(`/matters/${inv.matter_id}`)).data.matter;
          setMatter(m);
          const bi = (await api.get(`/matters/${inv.matter_id}/billable-items`, { params: { invoice_id: id } })).data;
          setItems(bi);
          setForm({
            invoice_number: inv.invoice_number, invoice_date: inv.invoice_date, due_date: inv.due_date,
            client: inv.client, client_code: inv.client_code || "", client_vat_number: inv.client_vat_number || "",
            client_address: inv.client_address || "", matter_id: inv.matter_id,
            language: inv.language, currency: inv.currency, vat_treatment: inv.vat_treatment,
            vat_note: inv.vat_note || "", payment_link: inv.payment_link || "",
            internal_notes: inv.internal_notes || "",
          });
          setLines(inv.lines.map((l) => ({ ...l, key: lineKey(l) })));
        } else {
          const matterId = params.get("matter");
          if (!matterId) {
            navigate("/invoices", { replace: true });
            return;
          }
          const [m, bi, nn] = await Promise.all([
            api.get(`/matters/${matterId}`),
            api.get(`/matters/${matterId}/billable-items`),
            api.get("/invoices/next-number"),
          ]);
          const matterData = m.data.matter;
          setMatter(matterData);
          setItems(bi.data);
          setForm({
            invoice_number: nn.data.invoice_number,
            invoice_date: todayIso(),
            due_date: addDaysIso(todayIso(), s.default_payment_term_days ?? 7),
            client: matterData.client || "", client_code: "", client_vat_number: "",
            client_address: "", matter_id: matterId,
            language: "LT", currency: "EUR", vat_treatment: "standard",
            vat_note: "", payment_link: "", internal_notes: "",
          });
        }
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [id]);

  const timeUnit = form?.language === "LT" ? "val." : "h";
  const pcUnit = form?.language === "LT" ? "vnt." : "pc";

  const defaultVatRate = (sourceType) => {
    if (sourceType === "expense") return 0;
    return form.vat_treatment === "standard" ? Number(settings?.default_vat_rate ?? 21) : 0;
  };

  const toggle = (entry, sourceType) => {
    const key = `${sourceType}-${entry.id}`;
    setLines((cur) => {
      if (cur.some((l) => l.key === key)) return cur.filter((l) => l.key !== key);
      const isTime = sourceType === "time";
      const isFixed = isTime && entry.billing_type === "Fixed Fee";
      const fixedVat = isFixed ? Number(entry.vat_rate ?? settings?.default_vat_rate ?? 21) : null;
      return [
        ...cur,
        {
          key, source_type: sourceType, source_id: entry.id, date: entry.date,
          description: entry.description,
          unit: isTime && !isFixed ? timeUnit : pcUnit,
          quantity: isFixed ? 1 : isTime ? Math.round((entry.billable_minutes / 60) * 100) / 100 : 1,
          rate: isFixed ? Number(entry.amount || 0)
            : isTime ? Number(entry.hourly_rate || 0) : Number(entry.amount || 0) + Number(entry.vat || 0),
          vat_rate: isFixed
            ? (form.vat_treatment === "standard" ? fixedVat : 0)
            : defaultVatRate(sourceType),
          entry_vat_rate: fixedVat,
        },
      ];
    });
  };

  const updateLine = (key, patch) =>
    setLines((cur) => cur.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const move = (key, dir) =>
    setLines((cur) => {
      const i = cur.findIndex((l) => l.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const totals = useMemo(() => {
    const withAmounts = lines.map((l) => ({
      ...l,
      amount: Math.round(l.quantity * l.rate * 100) / 100,
      vat_amount: Math.round(l.quantity * l.rate * (Number(l.vat_rate) || 0)) / 100,
    }));
    const fees = withAmounts.filter((l) => l.source_type === "time").reduce((s, l) => s + l.amount, 0);
    const exp = withAmounts.filter((l) => l.source_type === "expense").reduce((s, l) => s + l.amount, 0);
    const vat = withAmounts.reduce((s, l) => s + l.vat_amount, 0);
    return {
      fees: Math.round(fees * 100) / 100,
      exp: Math.round(exp * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round((fees + exp + vat) * 100) / 100,
    };
  }, [lines]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onTreatmentChange = (v) => {
    setForm((f) => ({
      ...f,
      vat_treatment: v,
      vat_note: v === "reverse_charge" && !f.vat_note ? RC_NOTE[f.language] : f.vat_note,
    }));
    setLines((cur) =>
      cur.map((l) => (l.source_type === "time"
        ? { ...l, vat_rate: v === "standard" ? Number(l.entry_vat_rate ?? settings?.default_vat_rate ?? 21) : 0 }
        : l))
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.invoice_date || !form.due_date) {
      setError(t("invoice.invalid_dates"));
      return;
    }
    if (lines.length === 0) {
      setError(t("invoice.select_one"));
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      lines: lines.map(({ key, entry_vat_rate, ...l }) => ({ ...l, quantity: Number(l.quantity), rate: Number(l.rate), vat_rate: Number(l.vat_rate) || 0 })),
    };
    try {
      const { data } = isEdit
        ? await api.put(`/invoices/${id}`, payload)
        : await api.post("/invoices", payload);
      toast.success(isEdit ? t("invoice.updated") : t("invoice.created", { number: data.invoice_number }));
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (error && !form) {
    return <div className="text-sm text-red-700" data-testid="invoice-form-load-error">{error}</div>;
  }
  if (!form || !matter) {
    return <div className="text-sm text-slate-500" data-testid="invoice-form-loading">{t("common.loading")}</div>;
  }

  const ItemList = ({ title, entries, sourceType, empty }) => (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h3>
      {entries.length === 0 ? (
        <div className="text-xs text-slate-400">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e) => {
            const key = `${sourceType}-${e.id}`;
            const checked = lines.some((l) => l.key === key);
            return (
              <label
                key={e.id}
                data-testid={`billable-item-${key}`}
                className={`flex items-center gap-3 border rounded-md px-3 py-2 cursor-pointer text-xs transition-colors ${
                  checked ? "border-slate-800 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(e, sourceType)} className="accent-slate-800" />
                <span className="text-slate-500 shrink-0">{formatDate(e.date)}</span>
                <span className="flex-1 truncate text-slate-800">{e.description}</span>
                <span className="shrink-0 text-slate-600 font-semibold">
                  {sourceType === "time"
                    ? e.billing_type === "Fixed Fee"
                      ? `${t("val.Fixed Fee")} · ${fmtMoney(e.amount)}`
                      : `${fmtDecimalHours(e.billable_minutes)} × ${fmtMoney(e.hourly_rate)}`
                    : fmtMoney(Number(e.amount || 0) + Number(e.vat || 0))}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="invoice-form-page" className="space-y-6 max-w-5xl">
      <div>
        <Link to={isEdit ? `/invoices/${id}` : "/invoices"} data-testid="invoice-form-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft className="h-3.5 w-3.5" /> {t("common.back")}
        </Link>
        <h1 className="font-serif text-3xl tracking-tight text-slate-900">
          {isEdit ? t("invoice.edit_draft", { number: form.invoice_number }) : t("invoice.new")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("invoice.matter_label")}: {matter.name}</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="invoice-form">
        {error && (
          <div data-testid="invoice-form-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("invoice.details")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <Label>{t("invoice.number")}</Label>
              <Input data-testid="invoice-number-input" required value={form.invoice_number}
                onChange={set("invoice_number")} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("invoice.date")}</Label>
              <div className="mt-1.5">
                <DateInput testid="invoice-date-input" value={form.invoice_date} onChange={set("invoice_date")} />
              </div>
            </div>
            <div>
              <Label>{t("invoice.due")}</Label>
              <div className="mt-1.5">
                <DateInput testid="invoice-due-date-input" value={form.due_date} onChange={set("due_date")} />
              </div>
            </div>
            <div>
              <Label>{t("invoice.currency")}</Label>
              <Input data-testid="invoice-currency-input" value={form.currency} onChange={set("currency")} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("invoice.language")}</Label>
              <Select value={form.language} onValueChange={set("language")}>
                <SelectTrigger data-testid="invoice-language-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LT" data-testid="invoice-language-lt">{t("invoice.lang_lt")}</SelectItem>
                  <SelectItem value="EN" data-testid="invoice-language-en">{t("invoice.lang_en")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("invoice.vat_treatment")}</Label>
              <Select value={form.vat_treatment} onValueChange={onTreatmentChange}>
                <SelectTrigger data-testid="invoice-vat-treatment" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard" data-testid="vat-treatment-standard">{t("val.standard")}</SelectItem>
                  <SelectItem value="zero" data-testid="vat-treatment-zero">{t("val.zero")}</SelectItem>
                  <SelectItem value="reverse_charge" data-testid="vat-treatment-reverse-charge">{t("val.reverse_charge")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>{t("invoice.payment_link")}</Label>
              <Input data-testid="invoice-payment-link-input" value={form.payment_link}
                onChange={set("payment_link")} placeholder="https://…" className="mt-1.5" />
            </div>
          </div>
          {form.vat_treatment === "reverse_charge" && (
            <div className="mt-4">
              <Label>{t("invoice.vat_note")}</Label>
              <Input data-testid="invoice-vat-note-input" value={form.vat_note} onChange={set("vat_note")} className="mt-1.5" />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <div className="col-span-2">
              <Label>{t("invoice.client")}</Label>
              <Input data-testid="invoice-client-input" required value={form.client} onChange={set("client")} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("invoice.client_code")}</Label>
              <Input data-testid="invoice-client-code-input" value={form.client_code} onChange={set("client_code")} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("invoice.client_vat")}</Label>
              <Input data-testid="invoice-client-vat-input" value={form.client_vat_number} onChange={set("client_vat_number")} className="mt-1.5" />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <Label>{t("invoice.client_address")}</Label>
              <Input data-testid="invoice-client-address-input" value={form.client_address} onChange={set("client_address")} className="mt-1.5" />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <Label>{t("invoice.internal_notes")}</Label>
              <Textarea data-testid="invoice-internal-notes-input" value={form.internal_notes}
                onChange={set("internal_notes")} rows={2} className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-5" data-testid="billable-items-panel">
          <h2 className="font-serif text-lg text-slate-800">{t("invoice.unbilled_items")}</h2>
          <ItemList title={t("invoice.unbilled_time")} entries={items.time_entries} sourceType="time" empty={t("invoice.no_time")} />
          <ItemList title={t("invoice.unbilled_expenses")} entries={items.expenses} sourceType="expense" empty={t("invoice.no_expenses")} />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="invoice-lines-panel">
          <h2 className="font-serif text-lg text-slate-800 mb-4">{t("invoice.lines", { n: lines.length })}</h2>
          {lines.length === 0 ? (
            <div className="text-sm text-slate-500" data-testid="no-invoice-lines">{t("invoice.no_lines")}</div>
          ) : (
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l.key} data-testid={`invoice-line-${l.key}`}
                  className="border border-slate-200 rounded-md px-3 py-2.5 grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 flex flex-col items-center gap-0.5">
                    <button type="button" data-testid={`line-up-${l.key}`} onClick={() => move(l.key, -1)} disabled={i === 0}
                      className="text-slate-400 hover:text-slate-800 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`line-down-${l.key}`} onClick={() => move(l.key, 1)} disabled={i === lines.length - 1}
                      className="text-slate-400 hover:text-slate-800 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="col-span-5">
                    <Input data-testid={`line-description-${l.key}`} value={l.description}
                      onChange={(e) => updateLine(l.key, { description: e.target.value })} className="h-8 text-xs" />
                    <div className="text-[10px] text-slate-400 mt-1">
                      {formatDate(l.date)} · {l.source_type === "time" ? t("invoice.time_entry") : t("invoice.expense")}
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`line-unit-${l.key}`} value={l.unit}
                      onChange={(e) => updateLine(l.key, { unit: e.target.value })} className="h-8 text-xs" title="Unit" />
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`line-quantity-${l.key}`} type="number" min="0" step="0.01" value={l.quantity}
                      onChange={(e) => updateLine(l.key, { quantity: e.target.value })} className="h-8 text-xs" title="Quantity" />
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`line-rate-${l.key}`} type="number" min="0" step="0.01" value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: e.target.value })} className="h-8 text-xs" title="Rate" />
                  </div>
                  <div className="col-span-1">
                    <Input data-testid={`line-vat-rate-${l.key}`} type="number" min="0" max="100" step="0.01" value={l.vat_rate}
                      onChange={(e) => updateLine(l.key, { vat_rate: e.target.value })} className="h-8 text-xs" title="VAT %" />
                  </div>
                  <div className="col-span-1 text-right text-xs font-semibold text-slate-800" data-testid={`line-amount-${l.key}`}>
                    {fmtMoney(Math.round(Number(l.quantity) * Number(l.rate) * 100) / 100)}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button type="button" data-testid={`line-remove-${l.key}`} onClick={() => toggle({ id: l.source_id }, l.source_type)}
                      className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 border-t border-slate-100 pt-4 space-y-1.5 text-sm max-w-xs ml-auto" data-testid="invoice-totals-preview">
            <div className="flex justify-between"><span className="text-slate-500">{t("invoice.totals_fees")}</span><span className="font-semibold">{fmtMoney(totals.fees)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("invoice.totals_vat")}</span><span className="font-semibold">{fmtMoney(totals.vat)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("invoice.totals_expenses")}</span><span className="font-semibold">{fmtMoney(totals.exp)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1.5">
              <span className="font-semibold text-slate-800">{t("invoice.totals_total")}</span>
              <span className="font-bold text-slate-900" data-testid="invoice-total-preview">{fmtMoney(totals.total)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" data-testid="invoice-form-cancel"
            onClick={() => navigate(isEdit ? `/invoices/${id}` : "/invoices")}>{t("common.cancel")}</Button>
          <Button type="submit" data-testid="invoice-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
            {saving ? t("common.saving") : isEdit ? t("invoice.save_draft") : t("invoice.create_draft")}
          </Button>
        </div>
      </form>
    </div>
  );
}
